import React from 'react';
import { Info } from 'lucide-react';
import { pickLocalizedText } from '../../../utils/localizedContent';

export default function LeagueCompetitionView({ bracket, onSelectMatch, hasEventsPermission, lang, participantsById = {} }) {
  const matches = bracket?.matches || [];
  const text = (es, en) => pickLocalizedText(lang, es, en);
  const rounds = Object.entries(
    matches.reduce((acc, m) => {
      const key = String(m.round || 1);
      acc[key] = acc[key] || [];
      acc[key].push(m);
      return acc;
    }, {})
  ).sort(([a], [b]) => Number(a) - Number(b));

  return (
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
      <div className="xl:col-span-2">
        <div className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory">
          {rounds.map(([roundNum, roundMatches]) => (
            <div key={roundNum} className="bg-card border border-border rounded-xl p-4 min-w-[300px] sm:min-w-[360px] snap-start shrink-0">
              <h4 className="text-primary font-semibold mb-3">{text('Ronda', 'Round')} {roundNum}</h4>
              <div className="space-y-2">
                {roundMatches.map((m) => {
                  const canReport = hasEventsPermission && m.status !== 'completed';
                  const p1 = m.player1 || {};
                  const p2 = m.player2 || {};
                  const full1 = participantsById[p1.user_id] || {};
                  const full2 = participantsById[p2.user_id] || {};
                  const avatar1 = full1.avatar;
                  const avatar2 = full2.avatar;
                  const initial1 = (full1.name || p1.name || 'T').charAt(0).toUpperCase();
                  const initial2 = (full2.name || p2.name || 'T').charAt(0).toUpperCase();
                  return (
                    <div key={m.match_id} className="bg-muted/50 rounded-lg px-3 py-2 flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0 text-foreground text-sm">
                        <div className="w-6 h-6 rounded-full bg-[#111827] border border-white/10 overflow-hidden flex items-center justify-center text-[10px] text-white">
                          {avatar1 ? (
                            <img src={avatar1} alt={full1.name || p1.name || 'Player 1'} className="w-full h-full object-cover" />
                          ) : (
                            initial1
                          )}
                        </div>
                        <span className="truncate">{p1.name}</span>
                        <span className="text-muted-foreground text-xs mx-1">vs</span>
                        <div className="w-6 h-6 rounded-full bg-[#111827] border border-white/10 overflow-hidden flex items-center justify-center text-[10px] text-white">
                          {avatar2 ? (
                            <img src={avatar2} alt={full2.name || p2.name || 'Player 2'} className="w-full h-full object-cover" />
                          ) : (
                            initial2
                          )}
                        </div>
                        <span className="truncate">{p2.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground text-xs">
                          {m.status === 'completed' ? `${m.score_p1}-${m.score_p2}` : text('Pendiente', 'Pending')}
                        </span>
                        {canReport && (
                          <button onClick={() => onSelectMatch(m)} className="text-xs text-primary">
                            {text('Reportar', 'Report')}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-card border border-border rounded-xl p-4 h-fit">
        <div className="flex items-center justify-between gap-2 mb-3">
          <h4 className="text-primary font-semibold">{text('Tabla de liga', 'League Table')}</h4>
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            title={text(
              'Desempates: Puntos → Golaverage → Enfrentamiento directo → Victorias → Derrotas',
              'Tiebreakers: Points → Goal difference → Head-to-head → Wins → Losses',
            )}
          >
            <Info size={14} />
            {text('Desempate', 'Tiebreak')}
          </button>
        </div>
        <div className="space-y-2">
          {(bracket?.standings || []).map((s) => {
            const p = s.player || {};
            const full = participantsById[p.user_id] || {};
            const avatar = full.avatar;
            const initial = (full.name || p.name || '?').charAt(0).toUpperCase();
            return (
              <div key={p.user_id} className="bg-muted/50 rounded-lg px-3 py-2 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-[#111827] border border-white/10 overflow-hidden flex items-center justify-center text-[11px] text-white">
                    {avatar ? (
                      <img src={avatar} alt={full.name || p.name || 'Player'} className="w-full h-full object-cover" />
                    ) : (
                      initial
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-foreground text-sm truncate">#{s.position} {p.name}</p>
                    <p className="text-muted-foreground text-xs">{s.wins}W-{s.draws || 0}D-{s.losses}L</p>
                  </div>
                </div>
                <span className="text-primary text-sm font-bold">{s.points}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
